/// <amd-module name="DistributedTaskUI/SecurityComponent/UserPermissionsAuthorization/Store/RoleAssignmentComponentSecurityStore" />
import * as React from "react";
import * as VSSStore from "VSS/Reflux/Store";
import Roles_Contracts = require("SecurityRoles/Client/Contracts/SecurityRoles");
import { RoleAssignmentComponentSecurityActionsHub } from "UserPermissionsAuthorization/Actions/RoleAssignmentComponentSecurityActionsHub";
import * as Utils_String from "VSS/Core/Util/String";
import * as Resources from "DistributedTaskUI/SecurityComponent/Resources";

export interface IRoleAssignmentSecurityStoreProps {
    userId: string;
    canEditProjectView?: boolean;
    manageRolesPermission?: number;
    roleScope: string;
    canEditOrganizationView: boolean;
}

export interface IRoleAssignmentSecurityGridRow {
    role: Roles_Contracts.RoleAssignment;
    displayName: string;
    access: string;
    accessValue: Roles_Contracts.RoleAccess;
    roleName: string;
    isDirty: boolean;
    isRemoved: boolean;
}

export interface IRoleAssignmentSecurityState {
    canEdit: boolean;
    inherit: boolean;
    roles: Roles_Contracts.SecurityRole[];
    roleAssignments: IRoleAssignmentSecurityGridRow[];
    selectedRole: IRoleAssignmentSecurityGridRow;
    isDirty: boolean;
    showAddIdentityPicker: boolean;
    roleScope: string;
    error: string;
}

export class RoleAssignmentComponentSecurityStore extends VSSStore.Store {
    constructor(actions: RoleAssignmentComponentSecurityActionsHub, storeProps: IRoleAssignmentSecurityStoreProps) {
        super("RoleAssignmentComponentSecurityStore");

        let canEdit: boolean = false, roleScope: string = "";
        if (typeof storeProps.canEditProjectView === "boolean") {
            // If value specified directly, use it
            canEdit = storeProps.canEditProjectView;
        }
        if (storeProps.roleScope) {
            roleScope = storeProps.roleScope;
        }

        this._state = {
            canEdit: canEdit,
            inherit: true,
            roles: [],
            roleAssignments: [],
            selectedRole: null,
            isDirty: false,
            showAddIdentityPicker: false,
            roleScope: roleScope,
            error: ""
        };

        this._actions = actions;
        this._storeProps = storeProps;

        this.initialize();
    }
    public initialize(): void {
        this._actions.addNewUser.addListener(this._onAddNewUser);
        this._actions.initializeRoles.addListener(this._onInitializeRoles);
        this._actions.initializeRoleAssignments.addListener(this._onInitializeRoleAssignments);
        this._actions.removeUser.addListener(this._onRemoveUser);
        this._actions.updateRoleAssignment.addListener(this._onUpdateRoleAssignment);
        this._actions.updateRoleScope.addListener(this._updateRoleScope);
        this._actions.setErrorMessage.addListener(this.setErrorMessage);
    }

    public getState(): IRoleAssignmentSecurityState {
        return this._state;
    }

    public getGridSource(): IRoleAssignmentSecurityGridRow[] {
        let source = <IRoleAssignmentSecurityGridRow[]>[];

        let currentRoles = this._roleAssignmentsList.getCurrentRoles();
        for (let i = 0; i < currentRoles.length; i++) {
            let currentRole = currentRoles[i];
            let removed = this._roleAssignmentsList.userIsRemoved(currentRole.identity.id);
            let dirty = removed || this._roleAssignmentsList.itemIsDirty(currentRole.identity.id);
            let accessDisplayName = this._getAccessDisplayName(currentRole.access);
            source.push(<IRoleAssignmentSecurityGridRow>{
                role: currentRole,
                displayName: currentRole.identity.displayName,
                access: accessDisplayName,
                accessValue: currentRole.access,
                roleName: currentRole.role.displayName,
                isDirty: dirty,
                isRemoved: removed
            });
        }
        return source.sort((a, b) => Utils_String.localeIgnoreCaseComparer(a.displayName, b.displayName));
    }

    public getUserRoleAssignment(): Roles_Contracts.RoleAssignment {
        return this._userRoleAssignment;
    }

    public userIsRemoved(userId: string): boolean {
        return this._roleAssignmentsList.userIsRemoved(userId);
    }

    public getRemovedUserIds(): string[] {
        return this._roleAssignmentsList.getRemovedUserIds();
    }

    public getDirtyRows(): IRoleAssignmentSecurityGridRow[] {
        let dirtyRows = [];
        let rows = this._state.roleAssignments;

        for (let i = 0; i < rows.length; i++) {
            let row = rows[i];
            if (row.isDirty) {
                dirtyRows.push(row);
            }
        }

        return dirtyRows;
    }

    private __dispose(): void {
        this._actions.addNewUser.removeListener(this._onAddNewUser);
        this._actions.initializeRoles.removeListener(this._onInitializeRoles);
        this._actions.initializeRoleAssignments.removeListener(this._onInitializeRoleAssignments);
        this._actions.removeUser.removeListener(this._onRemoveUser);
        this._actions.updateRoleAssignment.removeListener(this._onUpdateRoleAssignment);
        this._actions.updateRoleScope.removeListener(this._updateRoleScope);
    }

    private _getAccessDisplayName(access: Roles_Contracts.RoleAccess): string {
        switch (access) {
            case Roles_Contracts.RoleAccess.Assigned:
                return Resources.AccessAssigned;
            case Roles_Contracts.RoleAccess.Inherited:
                return Resources.AccessInherited;
            default:
                return "";
        }
    }

    private _onInitializeRoles = (roles: Roles_Contracts.SecurityRole[]): void => {
        this._state.roles = roles;
        this.emitChanged();
    }

    private _onInitializeRoleAssignments = (roleAssignments: Roles_Contracts.RoleAssignment[]): void => {
        this._roleAssignmentsList = new RoleAssignmentsList(roleAssignments);
        let userAssignedRole = this.getGridSource().filter(role => role.role.identity.id === this._storeProps.userId)[0];
        if (!!userAssignedRole) {
            this._userRoleAssignment = userAssignedRole.role;
        }

        if (typeof this._storeProps.canEditProjectView !== "boolean") {
            // Try to get edit behavior from role assignments
            if (!this._storeProps.canEditProjectView
                && (!this._storeProps.manageRolesPermission
                    || !this._userRoleAssignment
                    || (this._userRoleAssignment.role.allowPermissions & this._storeProps.manageRolesPermission) === this._storeProps.manageRolesPermission)) {
                this._state.canEdit = true;
            }
        }
        this._state.roleAssignments = this.getGridSource();
        this._state.inherit = roleAssignments.filter(r => r.access === Roles_Contracts.RoleAccess.Inherited).length > 0;
        this._state.isDirty = this.getDirtyRows().length > 0;
        this.emitChanged();
    }

    private _onAddNewUser = (roleAssignments: Roles_Contracts.RoleAssignment[]): void => {
        roleAssignments.forEach((roleAssignment: Roles_Contracts.RoleAssignment, index: number, array: Roles_Contracts.RoleAssignment[]) => {
            // If there is already a roleAssignment for the user, replace it with the new one
            this._roleAssignmentsList.updateItem(roleAssignment, true);
            // Ensure the new role assignment is marked as not dirty
            let row = this._findRowByUserId(roleAssignment.identity.id);
            if (row) {
                row.isDirty = false;
            }
        });

        this._state.roleAssignments = this.getGridSource();
        this._state.isDirty = this.getDirtyRows().length > 0;
        this.emitChanged();
    }

    private _onRemoveUser = (userId: string): void => {
        this._roleAssignmentsList.removeRoleAssignment(userId);

        this._state.roleAssignments = this.getGridSource();
        this._state.isDirty = this.getDirtyRows().length > 0;
        this.emitChanged();
    }

    private _onUpdateRoleAssignment = (updatedRoleAssignment: Roles_Contracts.RoleAssignment): void => {
        this._roleAssignmentsList.updateItem(updatedRoleAssignment);

        this._state.roleAssignments = this.getGridSource();
        this._state.isDirty = this.getDirtyRows().length > 0;
        this.emitChanged();
    }

    private _findRowByUserId(userId: string): IRoleAssignmentSecurityGridRow {
        let rowIndex = this._findRowIndexByUserId(userId);
        if (rowIndex >= 0) {
            return this._state.roleAssignments[rowIndex];
        }

        return null;
    }

    private _findRowIndexByUserId(userId: string): number {
        let rows = this._state.roleAssignments;

        for (let i = 0; i < rows.length; i++) {
            let row = <IRoleAssignmentSecurityGridRow>rows[i];
            if (row.role.identity.id === userId) {
                return i;
            }
        }
        return -1;
    }

    private _updateRoleScope = (scope: string): void => {
        this._state.roleScope = scope;
        this._state.roleAssignments = [];
        if (scope === Resources.OrganizationButtonText) {
            this._state.canEdit = this._storeProps.canEditOrganizationView;
        }
        else {
            this._state.canEdit = this._storeProps.canEditProjectView;
        }
        this.emitChanged();
    }

    private setErrorMessage = (errorMessage: string): void => {
        this._state.error = errorMessage;
        this.emitChanged();
    }

    private _state: IRoleAssignmentSecurityState;
    private _actions: RoleAssignmentComponentSecurityActionsHub;
    private _storeProps: IRoleAssignmentSecurityStoreProps;
    private _roleAssignmentsList: RoleAssignmentsList;
    private _userRoleAssignment: Roles_Contracts.RoleAssignment;
}

export class RoleAssignmentsList {
    private _removedUserIds: string[];
    private _initialRoles: Roles_Contracts.RoleAssignment[];
    private _currentRoles: Roles_Contracts.RoleAssignment[];
    private _initialRolesLookup = new Map();
    private _currentRolesLookup = new Map();

    constructor(initialRoles: Roles_Contracts.RoleAssignment[]) {
        this._initialRoles = initialRoles;
        this._currentRoles = initialRoles;
        this._removedUserIds = [];
        initialRoles.forEach((initialRole: Roles_Contracts.RoleAssignment) => {
            this._initialRolesLookup.set(initialRole.identity.id, initialRole);
            this._currentRolesLookup.set(initialRole.identity.id, initialRole);
        });
    }

    /**
     * Returns true if the role assignment for the user has been changed from its initial state or removed
     */
    public itemIsDirty(userId: string): boolean {
        return this._initialRolesLookup.get(userId).role.name !== this._currentRolesLookup.get(userId).role.name
            || this._removedUserIds.indexOf(userId) > -1;
    }

    /**
     * Adds or updates a role assignment
     * If clean is true, the item is updated without being marked as dirty
     */
    public updateItem(role: Roles_Contracts.RoleAssignment, clean?: boolean): void {
        this._removedUserIds = this._removedUserIds.filter(id => id !== role.identity.id);
        let userId = role.identity.id;
        this._removeItem(userId, clean);
        this._currentRoles.push(role);
        this._currentRolesLookup.set(userId, role);
        if (clean) {
            this._initialRoles.push(role);
            this._initialRolesLookup.set(userId, role);
        }
    }

    /**
     * interally used to removed a role assignment from the list of current role assignments
     * If clean is true, removes the role assignment from initial roles as well; the item is not marked as dirty
     */
    private _removeItem(userId: string, clean?: boolean): void {
        let role = this._currentRolesLookup.get(userId);
        this._currentRoles = this._currentRoles.filter(r => r.identity.id !== userId);
        this._currentRolesLookup.delete(userId);

        if (clean) {
            this._initialRoles = this._initialRoles.filter(r => r.identity.id !== userId);
            this._initialRolesLookup.delete(userId);
        }
    }

    /**
     * Add a role assignment to the queue of role assignments to be removed from the resource
     */
    public removeRoleAssignment(userId: string): void {
        this._removedUserIds.push(userId);
    }

    /**
     * Returns true if the user's role assignment has been queued for removal
     */
    public userIsRemoved(userId: string): boolean {
        return this._removedUserIds.filter(id => id === userId).length > 0;
    }
    /**
     * Returns the current role assignments in the control
     */
    public getCurrentRoles(): Roles_Contracts.RoleAssignment[] {
        return this._currentRoles;
    }

    /**
     * Returns the initial role assignments in the control
     * This should match the role assignments on the server
     */
    public getInitialRoles(): Roles_Contracts.RoleAssignment[] {
        return this._initialRoles;
    }
    /**
     * Returns a list of ids for role assignments queued for removal
     */
    public getRemovedUserIds(): string[] {
        return this._removedUserIds;
    }
}
